import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { vi } from 'date-fns/locale';
import { ExportColumn, exportToExcel } from '../services/exportService';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from './ToastNotification';
import { logger } from '../utils/logger';


export interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  onExport: (dateFrom: Date | null, dateTo: Date | null) => Promise<any[]>;
  columns: ExportColumn[];
  fileName: string;
  sheetName?: string;
  title?: string;
  description?: string;
  requireDateRange?: boolean;
}

export const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onClose,
  onExport,
  columns,
  fileName,
  sheetName,
  title = 'Xuất dữ liệu ra Excel',
  description,
  requireDateRange = true
}) => {
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  const handleExport = async () => {
    if (requireDateRange && (!dateFrom || !dateTo)) {
      setError('Vui lòng chọn khoảng thời gian');
      return;
    }

    if (dateFrom && dateTo && dateFrom > dateTo) {
      setError('Ngày bắt đầu phải nhỏ hơn hoặc bằng ngày kết thúc');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch data with date range
      const data = await onExport(dateFrom, dateTo);

      if (!data || data.length === 0) {
        setError('Không có dữ liệu để xuất');
        return;
      }

      // Export to Excel
      exportToExcel({
        columns,
        data,
        fileName,
        sheetName: sheetName || 'Sheet1',
        dateFrom,
        dateTo
      });

      showSuccess('Xuất Excel thành công!');
      onClose();
      // Reset form
      setDateFrom(null);
      setDateTo(null);
    } catch (err) {
      logger.error('Export error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Có lỗi xảy ra khi xuất dữ liệu';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDateFrom(null);
    setDateTo(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {requireDateRange ? (
            <>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
                <DatePicker
                  label="Từ ngày"
                  value={dateFrom}
                  onChange={(newValue) => setDateFrom(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
                <DatePicker
                  label="Đến ngày"
                  value={dateTo}
                  onChange={(newValue) => setDateTo(newValue)}
                  minDate={dateFrom || undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                />
              </LocalizationProvider>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Dữ liệu sẽ được xuất toàn bộ (không cần chọn khoảng thời gian)
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Hủy
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={loading || (requireDateRange && (!dateFrom || !dateTo))}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {loading ? 'Đang xuất...' : 'Xuất Excel'}
        </Button>
      </DialogActions>
      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Dialog>
  );
};

